import type { Product } from '../data/products'
import type { Industry } from '../data/industries'
import type { Solution } from '../data/solutions'
import type { Service } from '../data/services'
import type { CaseStudy } from '../data/caseStudies'
import type { Testimonial } from '../data/testimonials'
import type { BlogPost } from '../data/blog'

export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8099/api'
export const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '')

/** Resolves a relative path returned by the API (e.g. /uploads/team/x.jpg) to a full URL. */
export function assetUrl(path: string | null | undefined): string | null {
  if (!path) return null
  if (/^https?:\/\//.test(path)) return path
  return `${API_ORIGIN}${path}`
}

export interface TeamMember {
  id: number
  name: string
  title: string
  bio: string
  photoUrl: string | null
  linkedinUrl: string | null
}

export interface Settings {
  methodology: { step: string; name: string; description: string }[]
  techStack: { category: string; items: string[] }[]
  aiPlatformFeatures: { name: string; description: string }[]
  companyValues: { name: string; description: string }[]
  companyStats: { value: string; label: string }[]
  offices: { city: string; type: string }[]
  partners: { category: string; description: string }[]
  supportChannels: { name: string; description: string }[]
  resourceCategories: { name: string; description: string }[]
  trustBarLogos: string[]
  statHighlights: { value: string; label: string }[]
  blogCategories: string[]
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`)
  if (!res.ok) {
    throw new Error(`API request failed: ${path} (${res.status})`)
  }
  return res.json() as Promise<T>
}

export const api = {
  products: () => getJson<Product[]>('/products.php'),
  industries: () => getJson<Industry[]>('/industries.php'),
  solutions: () => getJson<Solution[]>('/solutions.php'),
  services: () => getJson<Service[]>('/services.php'),
  caseStudies: () => getJson<CaseStudy[]>('/case-studies.php'),
  testimonials: () => getJson<Testimonial[]>('/testimonials.php'),
  blogPosts: () => getJson<BlogPost[]>('/blog.php'),
  team: () => getJson<TeamMember[]>('/team.php'),
  settings: () => getJson<Settings>('/settings.php'),
}

export interface ContactPayload {
  name: string
  email: string
  company?: string
  phone?: string
  intent: string
  productInterest?: string
  message?: string
}

export async function postContact(payload: ContactPayload): Promise<void> {
  const res = await fetch(`${API_BASE}/contact.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error ?? 'Failed to send message')
  }
}
